import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { withProtectedRoute, roles, useAuth } from 'lib/auth'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import FileDownloadIcon from 'components/material-symbol/icons/FileDownload'
import Undo from 'components/material-symbol/icons/Undo'
import { useSnackbar } from 'notistack'
import { saveAs } from 'file-saver'
import { PageHeader } from 'components/page-header'
import AccountTransactionSummary from './account-transaction-summary'
import RefundDialog from './refund-dialog'
import DownloadReceiptDialog from './download-receipt-dialog'
import useGetRegistrationPayment from 'components/transaction-summary/use-get-registration-payment'
import LinearProgress from '@material-ui/core/LinearProgress'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import Error from 'components/error'
import { useStyles } from './styles'
import { canShowOnlineRefund } from '../transaction-helpers'

const TRANSACTIONS_PATH = '/account/payments/transactions'

const TransactionDetailPage = () => {
  const classes = useStyles()
  const { isCoach, user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const { transactionId } = useParams()
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const canDownloadReceipt = isCoach && !user?.hideSensitiveInformation
  const transactionsListPath = isCoach ? TRANSACTIONS_PATH : '/account/payments'
  const transactionListCrumb = [
    {
      label: 'Transactions',
      to: transactionsListPath,
    },
  ]
  const { data, loading, error, refetch } = useGetRegistrationPayment(
    transactionId,
  )

  const downloadReceipt = async () => {
    setIsDownloading(true)
    try {
      const { generateReceiptPdf } = await import('./generate-receipt-pdf')
      const blob = generateReceiptPdf({
        registrationPayment: data.registrationPayment,
        paymentSourceType: data.paymentSourceType,
        title: `Transaction #${transactionId} Receipt`,
      })
      saveAs(blob, `transaction-${transactionId}-receipt.pdf`)
    } catch (error) {
      window.rg4js &&
        window.rg4js('send', { error, tags: ['transaction-receipt'] })
      enqueueSnackbar('Failed to generate PDF', { variant: 'error' })
    } finally {
      setIsDownloading(false)
      setIsDownloadDialogOpen(false)
    }
  }

  if (loading) return <LinearProgress />
  if (error) return <Error error={error} />

  const { paymentSourceType, registrationPayment } = data
  const { products, transactions, cardBrand, last4 } = registrationPayment
  const leftToRefund = registrationPayment.leftToRefundInCents / 100.0
  const canRefund = canShowOnlineRefund({
    transactions,
    cardBrand,
    last4,
    leftToRefundInCents: registrationPayment.leftToRefundInCents,
    productTypeName: products[0].__typename,
    isCoach,
  })

  return (
    <>
      <PageHeader
        title={`Transaction #${transactionId}`}
        crumbs={transactionListCrumb}
        actions={
          canDownloadReceipt ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsDownloadDialogOpen(true)}
              startIcon={<FileDownloadIcon />}
            >
              Download PDF
            </Button>
          ) : null
        }
      />
      <DownloadReceiptDialog
        open={isDownloadDialogOpen}
        onClose={() => setIsDownloadDialogOpen(false)}
        onDownload={downloadReceipt}
        isDownloading={isDownloading}
        transactionId={transactionId}
      />
      <SensitiveInfoWrapper enableFallback>
        <Box className={classes.contentContainer}>
          <Box className={classes.stack}>
            <AccountTransactionSummary
              paymentSourceType={paymentSourceType}
              registrationPayment={registrationPayment}
            />
            {canRefund ? (
              <Box className={classes.refundCard}>
                <Box className={classes.refundCopy}>
                  <Typography className={classes.refundTitle}>
                    Need to reverse this transaction?
                  </Typography>
                  <Typography className={classes.refundCaption}>
                    Process a refund for the consumer or adjust the coaching
                    fee.
                  </Typography>
                </Box>
                <RefundDialog
                  trigger={
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.refundButton}
                      startIcon={<Undo className={classes.refundButtonIcon} />}
                    >
                      Add Refund
                    </Button>
                  }
                  registrationPaymentId={registrationPayment.id}
                  leftToRefund={leftToRefund}
                  onSuccess={refetch}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      </SensitiveInfoWrapper>
    </>
  )
}

export default withProtectedRoute(TransactionDetailPage, [
  roles.COACH,
  roles.CONSUMER,
])
